import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { Box, Button, Stack, Typography } from '@mui/material';

import { INTAKE_STATUS_STAGE_LABEL } from '../four-stage/IntakeJourneyShell';
import { INTAKE_STATUS_TAB_LABELS } from '../four-stage/IntakeStatusDashboard';

import { cardSx, wf } from './tokens';

interface IntakeCompletionPageProps {
  /** Exit for a customer whose intake is not hosted by the journey (no Intake Status step). */
  onGoToDashboard?: () => void;
  /**
   * Where the customer goes next inside the journey. Supplied only by a host that renders the
   * Intake Status step; it replaces the dashboard exit.
   */
  nextSteps?: {
    /** The practice's word for a patient, singular lower-case. */
    subjectLabel: string;
    onGoToIntakeStatus: () => void;
    /** Only when the practice chose EHR Migration, so its track exists. */
    onGoToEhrImport?: () => void;
  };
  /**
   * Group names on the organization's active trailing (post-critical) canvases.
   * Empty means this organization has no trailing page at all, so the page must
   * not promise a follow-up.
   */
  trailingItemLabels?: string[];
  /** True while at least one answerable trailing field is still unanswered. */
  trailingItemsRemain?: boolean;
  /** Opens the trailing page. Omitted when there is nothing to open. */
  onGoToTrailingItems?: () => void;
}

/** "a", "a and b", "a, b, and c" — the trailing group names, read as a sentence. */
function joinLabels(labels: string[]): string {
  if (labels.length <= 1) return labels[0] ?? '';
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels.at(-1)}`;
}

export function IntakeCompletionPage({
  onGoToDashboard,
  nextSteps,
  trailingItemLabels = [],
  trailingItemsRemain = false,
  onGoToTrailingItems,
}: IntakeCompletionPageProps) {
  // The promise is derived, never fixed: the old sentence named professional
  // references and payer portal logins for every organization, including the
  // ones whose generated form never carried either group.
  const followUpLabels = trailingItemLabels.filter(
    label => label !== 'Desired enrollment start date',
  );
  const hasTrailingPage = followUpLabels.length > 0;
  const trailingSentence = joinLabels(followUpLabels);

  return (
    <Box
      role="status"
      aria-labelledby="intake-completion-heading"
      sx={{
        width: '100%',
        maxWidth: '760px',
        mx: 'auto',
        pt: { xs: 3, sm: 7 },
        pb: 4,
        px: { xs: 0, sm: 2 },
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          mx: 'auto',
          mb: 2.5,
          borderRadius: '50%',
          bgcolor: wf.greenSoft,
          color: wf.green,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CheckRoundedIcon aria-hidden="true" sx={{ fontSize: 36 }} />
      </Box>

      <Typography
        id="intake-completion-heading"
        component="h1"
        sx={{ color: wf.ink, fontSize: { xs: '24px', sm: '28px' }, fontWeight: 750 }}
      >
        Everything critical is in
      </Typography>
      <Typography
        sx={{
          color: wf.muted,
          fontSize: { xs: '15px', sm: '16px' },
          lineHeight: 1.45,
          mt: 1.75,
          mx: 'auto',
          maxWidth: '700px',
        }}
      >
        Our team reviews your items (typically 1–2 days), then starts filing with your payers. Payer
        review itself is set by the payers — 30 to 120+ days each; we&apos;ll show you exactly where
        things stand.
      </Typography>

      <Box
        sx={{
          ...cardSx,
          mt: 2.5,
          px: { xs: 2, sm: 2.5 },
          py: 2,
          textAlign: 'center',
        }}
      >
        <Typography component="h2" sx={{ color: wf.ink, fontSize: '17px', fontWeight: 700 }}>
          {!hasTrailingPage
            ? 'Nothing else is needed from you'
            : trailingItemsRemain
              ? 'A few items can follow later'
              : 'Your later items are already in'}
        </Typography>
        <Typography sx={{ color: wf.muted, fontSize: '15px', lineHeight: 1.45, mt: 0.75 }}>
          {!hasTrailingPage
            ? 'Your intake asks nothing further of you. If a payer needs something new, we will come back to you directly.'
            : trailingItemsRemain
              ? 'We will still need:'
              : `You have already added ${trailingSentence}. Nothing is outstanding.`}
        </Typography>
        {hasTrailingPage && trailingItemsRemain && (
          <>
            <Box
              component="ul"
              sx={{
                width: 'fit-content',
                mx: 'auto',
                my: 1.5,
                pl: 2.5,
                listStyleType: 'disc',
                textAlign: 'left',
                color: wf.muted,
                fontSize: '15px',
                lineHeight: 1.6,
              }}
            >
              {followUpLabels.map(label => (
                <Box component="li" key={label}>
                  {label}
                </Box>
              ))}
            </Box>
            <Typography sx={{ color: wf.muted, fontSize: '15px', lineHeight: 1.45 }}>
              Add them whenever you like — they do not hold up your filing.
            </Typography>
          </>
        )}
        {hasTrailingPage && onGoToTrailingItems && (
          <Button variant="outlined" onClick={onGoToTrailingItems} sx={{ mt: 2, fontWeight: 700 }}>
            {trailingItemsRemain ? 'Go to' : 'Review'} Items we&apos;ll need later
          </Button>
        )}
      </Box>

      {nextSteps ? (
        <>
          <Typography sx={{ color: wf.muted, fontSize: '15px', lineHeight: 1.45, mt: 2.5 }}>
            See how things are progressing in {INTAKE_STATUS_STAGE_LABEL}
            {nextSteps.onGoToEhrImport ? (
              <>
                , or go to {INTAKE_STATUS_TAB_LABELS['ehr-import']} to upload your{' '}
                {nextSteps.subjectLabel} data.
              </>
            ) : (
              '.'
            )}
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ mt: 2, justifyContent: 'center' }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={nextSteps.onGoToIntakeStatus}
              sx={{ minWidth: { sm: 220 }, fontWeight: 700 }}
            >
              Go to {INTAKE_STATUS_STAGE_LABEL}
            </Button>
            {nextSteps.onGoToEhrImport && (
              <Button
                variant="outlined"
                size="large"
                onClick={nextSteps.onGoToEhrImport}
                sx={{ minWidth: { sm: 220 }, fontWeight: 700 }}
              >
                Go to {INTAKE_STATUS_TAB_LABELS['ehr-import']}
              </Button>
            )}
          </Stack>
        </>
      ) : onGoToDashboard ? (
        <Button
          variant="contained"
          size="large"
          onClick={onGoToDashboard}
          sx={{ mt: 2.5, minWidth: { xs: '100%', sm: 220 }, fontWeight: 700 }}
        >
          Go to your dashboard
        </Button>
      ) : (
        <Typography sx={{ color: wf.muted, fontSize: '13px', mt: 2.5 }}>
          This completion is recorded for the selected provider. Use the admin navigation to
          continue.
        </Typography>
      )}
    </Box>
  );
}
